<template>
  <div class="container mx-auto px-4 py-8 max-w-3xl">
    <h1 class="text-3xl font-bold text-gray-800 mb-8">
      {{ menu.id ? "Redigera avsmakningsmeny" : "Skapa avsmakningsmeny" }}
    </h1>

    <div
      v-if="errors && errors.length"
      class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
    >
      <p v-for="error in errors" :key="error" class="text-red-700 text-sm">
        {{ error }}
      </p>
    </div>

    <form @submit.prevent="submit">
      <!-- Title -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Titel *
        </label>
        <input
          v-model="form.title"
          type="text"
          required
          class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-emerald-400 focus:outline-none focus:ring-0"
          placeholder="T.ex. Sommarmeny 2026"
        />
      </div>

      <!-- Description -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Beskrivning
        </label>
        <textarea
          v-model="form.description"
          rows="3"
          class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-emerald-400 focus:outline-none focus:ring-0"
          placeholder="En kort beskrivning av menyn..."
        ></textarea>
      </div>

      <!-- Number of courses selector -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Antal rätter
        </label>
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-lg font-bold cursor-pointer"
            :disabled="form.courses.length <= 1"
            @click="removeCourse"
          >
            −
          </button>
          <span class="text-2xl font-bold text-gray-800 w-8 text-center">
            {{ form.courses.length }}
          </span>
          <button
            type="button"
            class="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center text-lg font-bold cursor-pointer"
            @click="addCourse"
          >
            +
          </button>
        </div>
      </div>

      <!-- Courses -->
      <div class="flex flex-col gap-4 mb-8">
        <div
          v-for="(course, index) in form.courses"
          :key="index"
          class="bg-white rounded-lg shadow p-5 border-l-4 border-emerald-500"
        >
          <div class="flex justify-between items-center mb-3">
            <span class="text-sm font-semibold text-emerald-700">
              Rätt {{ index + 1 }}
            </span>
          </div>

          <!-- Course name -->
          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Namn på rätt
            </label>
            <input
              v-model="course.courseName"
              type="text"
              required
              class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-emerald-400 focus:outline-none focus:ring-0"
              :placeholder="defaultCourseNames[index] || `Rätt ${index + 1}`"
            />
          </div>

          <!-- Recipe selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Välj recept
            </label>
            <select
              v-model="course.recipeId"
              required
              class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-emerald-400 focus:outline-none focus:ring-0"
            >
              <option value="" disabled>Välj ett recept...</option>
              <option
                v-for="recipe in recipes"
                :key="recipe.id"
                :value="recipe.id"
              >
                {{ recipe.title }}
              </option>
            </select>
            <p
              v-if="course.recipeId && selectedRecipeIngress(course.recipeId)"
              class="text-sm text-gray-500 mt-1 italic"
            >
              {{ selectedRecipeIngress(course.recipeId) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Submit buttons -->
      <div class="flex justify-between items-center">
        <Link
          :href="tastingMenusPath"
          class="text-gray-500 hover:text-gray-700"
        >
          Avbryt
        </Link>
        <button
          type="submit"
          :disabled="form.processing"
          class="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-50"
        >
          {{ menu.id ? "Spara ändringar" : "Skapa meny" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { Link, router, useForm } from "@inertiajs/vue3";

const props = defineProps({
  menu: Object,
  recipes: Array,
  currentUser: Object,
  actionPath: String,
  tastingMenusPath: String,
  errors: Array,
});

const defaultCourseNames = [
  "Förrätt",
  "Varmrätt",
  "Dessert",
  "Petit four",
  "Amuse-bouche",
];

const form = useForm({
  title: props.menu.title ?? "",
  description: props.menu.description ?? "",
  courses:
    props.menu.courses.length > 0
      ? props.menu.courses.map((c) => ({ ...c }))
      : [{ id: null, position: 1, courseName: "", recipeId: "" }],
});

function addCourse() {
  form.courses.push({
    id: null,
    position: form.courses.length + 1,
    courseName: "",
    recipeId: "",
  });
}

function removeCourse() {
  if (form.courses.length > 1) {
    form.courses.pop();
  }
}

function selectedRecipeIngress(recipeId) {
  const recipe = props.recipes.find((r) => r.id === recipeId);
  return recipe?.ingress;
}

function submit() {
  const data = {
    title: form.title,
    description: form.description,
    tasting_menu_courses_attributes: form.courses.map((c, i) => ({
      ...(c.id ? { id: c.id } : {}),
      recipe_id: c.recipeId,
      position: i + 1,
      course_name: c.courseName,
    })),
  };

  if (props.menu.id) {
    router.patch(props.actionPath, data);
  } else {
    router.post(props.actionPath, data);
  }
}
</script>
